import React, { useCallback } from 'react';
import { Asset, CalendarMinimalIcon, InfoFilledIcon } from '@clearcover/atlas';

import { usePaymentScheduleModal } from 'V2/Hooks/usePaymentScheduleModal';
import { Button } from 'V2/Shared/Button';
import { isPayInFullPlan } from 'V2/Utilities/PaymentPlan';
import { PaymentPlanType } from 'V2/Types/Payment';

import { useFeeBreakdownModal } from './FeeBreakdownModal';
import { PaymentPlanSummaryProps } from './types';
import { buildDownPaymentFeeLines, buildInstallmentFeeLines } from './utils';
import styles from './styles.module.css';

export const PaymentPlanSummary: React.FC<PaymentPlanSummaryProps> = ({
  dueToday,
  futurePaymentAmount,
  paymentFrequency,
  totalCost,
  paymentsMadeOn,
  paymentPlan,
  showFeeBreakdown,
  goToQuoteSummary,
}) => {
  const {
    PaymentScheduleModal,
    showPaymentScheduleModal,
    closePaymentScheduleModal,
  } = usePaymentScheduleModal();

  const { FeeBreakdownModalComponent, showFeeBreakdownModal } =
    useFeeBreakdownModal();

  const isPayInFullPaymentPlan = isPayInFullPlan(paymentPlan);

  const getNextPaymentDueDate = () => {
    const { paymentSchedule } = paymentPlan;
    const [, firstInstallment] = paymentSchedule;

    if (!firstInstallment) return undefined;

    return firstInstallment.date;
  };

  const getSectionsForInstallmentPlan = useCallback(
    (paymentPlan: PaymentPlanType) => {
      const downPaymentItems = buildDownPaymentFeeLines(paymentPlan);
      const installmentItems = buildInstallmentFeeLines(paymentPlan);

      return [
        {
          sectionName: 'downpayment',
          title: 'Included in down payment:',
          items: downPaymentItems,
        },
        {
          sectionName: 'installments',
          title: 'Included in installments:',
          items: installmentItems,
        },
      ];
    },
    []
  );

  const getSectionsForPayInFullPlan = useCallback(
    (paymentPlan: PaymentPlanType) => {
      const downPaymentItems = buildDownPaymentFeeLines(paymentPlan);

      return [
        {
          sectionName: 'downpayment',
          title: 'Included in down payment:',
          items: downPaymentItems,
        },
      ];
    },
    []
  );

  return (
    <div className={styles.paymentPlanSummary}>
      <div className={styles.paymentPlanSummaryHeader}>
        <div className={styles.paymentPlanSummaryTitle}>
          Payment plan summary
        </div>
        <div>
          <Button
            variant="link"
            onClick={goToQuoteSummary}
            data-testid="purchase-edit-btn"
          >
            Edit
          </Button>
        </div>
      </div>
      {dueToday && (
        <div className={styles.purchaseSummaryRow}>
          <div>Due today</div>
          <div
            className={styles.paymentPlanSummaryRowValue}
            data-testid="due-today"
          >
            {dueToday}
          </div>
        </div>
      )}
      <div className={styles.purchaseSummaryRow}>
        <div>Payment frequency</div>
        <div
          className={styles.paymentPlanSummaryRowValue}
          data-testid="payment-frequency"
        >
          {paymentFrequency}
        </div>
      </div>
      {paymentsMadeOn && (
        <div className={styles.purchaseSummaryRow}>
          <div>Payments made on</div>
          <div
            className={styles.paymentPlanSummaryRowValue}
            data-testid="payment-day-of-week"
          >
            {paymentsMadeOn}
          </div>
        </div>
      )}
      {futurePaymentAmount && (
        <div className={styles.purchaseSummaryRow}>
          <div>Future payments</div>
          <div
            className={styles.paymentPlanSummaryRowValue}
            data-testid="future-payments"
          >
            {futurePaymentAmount}
          </div>
        </div>
      )}
      {!isPayInFullPaymentPlan && (
        <div className={styles.purchaseSummaryRow}>
          <div>Next payment due</div>
          <div
            className={[
              styles.paymentPlanSummaryRowValue,
              styles.paymentScheduleDueDate,
            ].join(' ')}
            data-testid="next-payment-due"
          >
            <Button
              data-testid="payment-plan-summary-button"
              variant="link"
              size="sm"
              type="button"
              onClick={() => {
                showPaymentScheduleModal({
                  paymentPlan,
                  onClose: closePaymentScheduleModal,
                });
              }}
            >
              <Asset
                icon={CalendarMinimalIcon}
                description="Payment schedule info"
                data-testid="payment-schedule-info-icon"
                height={16}
                width={16}
              />
            </Button>
            {getNextPaymentDueDate()}
          </div>
        </div>
      )}
      <div className={styles.purchaseSummaryRow}>
        <div className={styles.totalCostRow}>
          Total cost
          {showFeeBreakdown && (
            <Button
              data-testid="fee-breakdown-button"
              variant="link"
              size="sm"
              type="button"
              onClick={() => {
                showFeeBreakdownModal({
                  sections: isPayInFullPaymentPlan
                    ? getSectionsForPayInFullPlan(paymentPlan)
                    : getSectionsForInstallmentPlan(paymentPlan),
                });
              }}
            >
              <Asset
                icon={InfoFilledIcon}
                description="Fee breakdown info"
                data-testid="fee-breakdown-info-icon"
                height={16}
                width={16}
              />
            </Button>
          )}
        </div>
        <div
          className={styles.paymentPlanSummaryRowValue}
          data-testid="total-cost"
        >
          {totalCost}
        </div>
      </div>
      {PaymentScheduleModal}
      {FeeBreakdownModalComponent}
    </div>
  );
};
